import React, { useEffect, useMemo, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

import {
  PRACTICE_MANAGEMENT_ENDPOINT,
  PRACTICE_MANAGEMENT_ENDPOINTS,
  PracticeManagementVersionInfo,
  addNewCountry,
  createPracticeManagementRecord,
  deletePracticeManagementRecord,
  publishPracticeManagementDraft,
  resetCountryToGlobal,
  resetPracticeManagementDraft,
  updatePracticeManagementRecord,
} from "services";

import {
  checkAdminAccess,
  flattenToDotNotation,
  hasAdminTierEqualOrAbove,
  unflattenFromDotNotation,
  useAdminUserContext,
  useGetPracticeCrops,
  useGetPracticeManagementVersionNotes,
  useGetPracticeManagementVersions,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticePlantProtections,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useNavigate,
  useParams,
  usePracticeManagementCropEmissionFactors,
  usePracticeManagementCropParameters,
  usePracticeManagementMineralFertilizerEmissionFactors,
  usePracticeManagementOrganicFertilizerEmissionFactors,
  usePracticeManagementOrganicFertilizerParameters,
  usePracticeManagementPigletParameters,
  usePracticeManagementPlantProtectionEmissionFactors,
  usePracticeManagementPorkParameters,
  usePracticeManagementSeedingEmissionFactors,
  usePracticeManagementSoilEmissionFactors,
  usePracticeManagementTillageEmissionFactors,
  usePracticeManagementUtilityEmissionFactors,
} from "utils";

import {
  PRACTICE_MANAGEMENT,
  PRACTICE_MANAGEMENT_CROP_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_CROP_PARAMETERS,
  PRACTICE_MANAGEMENT_MINERAL_FERTILIZER_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_PARAMETERS,
  PRACTICE_MANAGEMENT_PIGLET_PARAMETERS,
  PRACTICE_MANAGEMENT_PLANT_PROTECTION_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_PORK_PARAMETERS,
  PRACTICE_MANAGEMENT_SEEDING_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_SOIL_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_TABS,
  PRACTICE_MANAGEMENT_TILLAGE_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_UTILITY_EMISSION_FACTORS,
  PRACTICE_MANAGEMENT_VERSION_DIFFERENCES,
} from "consts";

import { AlertBanner, ErrorBoundary, MainPage, Table, Tabs, Typography, Message as message } from "components";

import { PracticeManagementTable } from "pages/admin/practiceManagementService/PracticeManagementTable";
import { PublishButton } from "pages/admin/practiceManagementService/PublishButton";

type SaveDraftArgs = { data: Record<string, unknown>[]; idsToDelete: { id: string; country: PracticeManagement.CountryCode }[] };

type VersionProps = {
  version: string | undefined;
  versions: string[];
  isDraftVersion: boolean;
  hasDraftVersion: boolean;
  onVersionChange: (v: string) => void;
  selectedCountry: PracticeManagement.CountryCode;
  onCountryChange: (c: PracticeManagement.CountryCode) => void;
  onPublish: React.ComponentProps<typeof PublishButton>["onPublish"];
  onResetDraft: () => Promise<void>;
  onCopyFromGlobal: () => Promise<void>;
  onResetToGlobal: (endpoint: PRACTICE_MANAGEMENT_ENDPOINT) => Promise<void>;
};

const stripInternalFields = (row: Record<string, unknown>) => {
  const { name: _name, editingKey: _editingKey, version: _version, ...payload } = row;
  return { payload };
};

const useSaveDraft = (endpoint: string, queryKeySuffix: string, idField?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idsToDelete }: SaveDraftArgs) => {
      const deleteOps = idField ? idsToDelete.map(({ id, country }) => deletePracticeManagementRecord(endpoint, id, country)) : [];
      const writeOps = data.map((row) => {
        const { payload } = stripInternalFields(row);
        const country = (payload.country as PracticeManagement.CountryCode) ?? "global";
        if (!idField) {
          // Identifier-less resource: PUT /<resource>?country= (no /:id)
          return updatePracticeManagementRecord(endpoint, undefined, payload, country);
        }
        const recordId = row[idField] as string;
        return (row.editingKey as string)?.startsWith("new-")
          ? createPracticeManagementRecord(endpoint, payload, country)
          : updatePracticeManagementRecord(endpoint, recordId, payload, country);
      });
      return Promise.all([...deleteOps, ...writeOps]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT, queryKeySuffix] });
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT_VERSION_DIFFERENCES] });
      message({ content: "Draft saved successfully", icon: "tick" });
    },
    onError: () => message({ content: "Failed to save draft", icon: "error" }),
  });
};

const usePublishDraft = () => {
  const { user } = useAdminUserContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formValues: PracticeManagementVersionInfo) => {
      if (!user) throw new Error("Cannot publish: no authenticated user");
      return publishPracticeManagementDraft({ email: user.email, userId: user.id, ...formValues });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT] });
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT_VERSION_DIFFERENCES] });
      message({ content: "Draft published successfully", icon: "tick" });
    },
    onError: () => message({ content: "Failed to publish draft", icon: "error" }),
  });
};

const useResetDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => resetPracticeManagementDraft(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT] });
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT_VERSION_DIFFERENCES] });
      message({ content: "Draft reset to latest published version", icon: "tick" });
    },
    onError: () => message({ content: "Failed to reset draft", icon: "error" }),
  });
};

const useAddNewCountry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (country: PracticeManagement.CountryCode) => addNewCountry(country),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT] });
      message({ content: "Country draft populated from global", icon: "tick" });
    },
    onError: () => message({ content: "Failed to populate country draft", icon: "error" }),
  });
};

const useResetToGlobal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ country, endpoint }: { country: PracticeManagement.CountryCode; endpoint: PRACTICE_MANAGEMENT_ENDPOINT }) =>
      resetCountryToGlobal(country, endpoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT] });
      queryClient.invalidateQueries({ queryKey: [PRACTICE_MANAGEMENT_VERSION_DIFFERENCES] });
      message({ content: "Table reset to global", icon: "tick" });
    },
    onError: () => message({ content: "Failed to reset table to global", icon: "error" }),
  });
};

const CropParametersTable: React.FC<VersionProps> = ({ selectedCountry, onCountryChange, onPublish, onResetDraft, onCopyFromGlobal, onResetToGlobal }) => {
  const pmsQuery = usePracticeManagementCropParameters(selectedCountry);
  const practiceCropsQuery = useGetPracticeCrops({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.CROP_PARAMETERS, PRACTICE_MANAGEMENT_CROP_PARAMETERS, "cropId");

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.CROP_PARAMETERS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practiceCropsQuery.data?.find(({ id }) => id === pmsv.cropId)?.name, ...pmsv }))}
      identifierField="cropId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={["draft"]}
      selectedVersion={"draft"}
      isDraftVersion={true}
      hasDraftVersion={false}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const CropEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementCropEmissionFactors(version, selectedCountry);
  const practiceCropsQuery = useGetPracticeCrops({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.CROP_EMISSION_FACTORS, PRACTICE_MANAGEMENT_CROP_EMISSION_FACTORS, "cropId");
  // Memoised as the table resets its rows (dropping unsaved new ones) whenever `data` changes identity
  const tableData = useMemo(
    () =>
      (pmsQuery.data ?? []).map(
        (pmsv) =>
          flattenToDotNotation({ name: practiceCropsQuery.data?.find(({ id }) => id === pmsv.cropId)?.name, ...pmsv }) as {
            editingKey: string;
          } & Record<string, unknown>
      ),
    [pmsQuery.data, practiceCropsQuery.data]
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.CROP_EMISSION_FACTORS}
      data={tableData}
      identifierField="cropId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data: data.map(unflattenFromDotNotation), idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const MineralFertilizerEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementMineralFertilizerEmissionFactors(version, selectedCountry);
  const practiceMineralFertilizersQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.MINERAL_FERTILIZER_EMISSION_FACTORS,
    PRACTICE_MANAGEMENT_MINERAL_FERTILIZER_EMISSION_FACTORS,
    "fertilizerId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.MINERAL_FERTILIZER_EMISSION_FACTORS}
      data={
        pmsQuery.data
          ? pmsQuery.data.map(
              (pmsv) =>
                flattenToDotNotation({ name: practiceMineralFertilizersQuery.data?.find(({ id }) => id === pmsv.fertilizerId)?.name, ...pmsv }) as {
                  editingKey: string;
                } & Record<string, unknown>
            )
          : []
      }
      loading={pmsQuery.isLoading}
      identifierField="fertilizerId"
      onSaveDraft={(data, idsToDelete) => saveDraft({ data: data.map(unflattenFromDotNotation), idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const OrganicFertilizerEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementOrganicFertilizerEmissionFactors(version, selectedCountry);
  const practiceOrganicFertilizersQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_EMISSION_FACTORS,
    PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_EMISSION_FACTORS,
    "fertilizerId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_EMISSION_FACTORS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practiceOrganicFertilizersQuery.data?.find(({ id }) => id === pmsv.fertilizerId)?.name, ...pmsv }))}
      identifierField="fertilizerId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const OrganicFertilizerParametersTable: React.FC<VersionProps> = ({
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementOrganicFertilizerParameters(selectedCountry);
  const practiceOrganicFertilizersQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_PARAMETERS,
    PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_PARAMETERS,
    "fertilizerId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_PARAMETERS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practiceOrganicFertilizersQuery.data?.find(({ id }) => id === pmsv.fertilizerId)?.name, ...pmsv }))}
      identifierField="fertilizerId"
      loading={pmsQuery.isLoading || practiceOrganicFertilizersQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={["draft"]}
      selectedVersion={"draft"}
      isDraftVersion={true}
      hasDraftVersion={false}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const PlantProtectionEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementPlantProtectionEmissionFactors(version, selectedCountry);
  const practicePlantProtectionsQuery = useGetPracticePlantProtections();
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.PLANT_PROTECTION_EMISSION_FACTORS,
    PRACTICE_MANAGEMENT_PLANT_PROTECTION_EMISSION_FACTORS,
    "plantProtectionId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.PLANT_PROTECTION_EMISSION_FACTORS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practicePlantProtectionsQuery.data?.find(({ id }) => id === pmsv.plantProtectionId)?.name, ...pmsv }))}
      identifierField="plantProtectionId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const SeedingEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementSeedingEmissionFactors(version, selectedCountry);
  const practiceSeedingsQuery = useGetPracticeSeedings({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.SEEDING_EMISSION_FACTORS,
    PRACTICE_MANAGEMENT_SEEDING_EMISSION_FACTORS,
    "seedingId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.SEEDING_EMISSION_FACTORS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practiceSeedingsQuery.data?.find(({ id }) => id === pmsv.seedingId)?.name, ...pmsv }))}
      identifierField="seedingId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const TillageEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementTillageEmissionFactors(version, selectedCountry);
  const practiceTillagesQuery = useGetPracticeTillages({ enabled: true });
  const { mutateAsync: saveDraft } = useSaveDraft(
    PRACTICE_MANAGEMENT_ENDPOINTS.TILLAGE_EMISSION_FACTORS,
    PRACTICE_MANAGEMENT_TILLAGE_EMISSION_FACTORS,
    "tillageId"
  );

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.TILLAGE_EMISSION_FACTORS}
      data={(pmsQuery.data ?? []).map((pmsv) => ({ name: practiceTillagesQuery.data?.find(({ id }) => id === pmsv.tillageId)?.name, ...pmsv }))}
      identifierField="tillageId"
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const SoilEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementSoilEmissionFactors(version, selectedCountry);
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.SOIL_EMISSION_FACTORS, PRACTICE_MANAGEMENT_SOIL_EMISSION_FACTORS);

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.SOIL_EMISSION_FACTORS}
      data={pmsQuery.data ? [pmsQuery.data] : []}
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const UtilityEmissionFactorsTable: React.FC<VersionProps> = ({
  version,
  versions,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
}) => {
  const pmsQuery = usePracticeManagementUtilityEmissionFactors(version, selectedCountry);
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.UTILITY_EMISSION_FACTORS, PRACTICE_MANAGEMENT_UTILITY_EMISSION_FACTORS);

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.UTILITY_EMISSION_FACTORS}
      data={pmsQuery.data ? [flattenToDotNotation(pmsQuery.data) as { editingKey: string } & Record<string, unknown>] : []}
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data: data.map(unflattenFromDotNotation), idsToDelete })}
      onPublish={onPublish}
      versions={versions}
      selectedVersion={version}
      isDraftVersion={isDraftVersion}
      hasDraftVersion={hasDraftVersion}
      onVersionChange={onVersionChange}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const PigletParametersTable: React.FC<VersionProps> = ({ selectedCountry, onCountryChange, onPublish, onResetDraft, onCopyFromGlobal, onResetToGlobal }) => {
  const pmsQuery = usePracticeManagementPigletParameters(selectedCountry);
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.PIGLET_PARAMETERS, PRACTICE_MANAGEMENT_PIGLET_PARAMETERS);

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.PIGLET_PARAMETERS}
      data={pmsQuery.data ? [pmsQuery.data] : []}
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={["draft"]}
      selectedVersion={"draft"}
      isDraftVersion={true}
      hasDraftVersion={false}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const PorkParametersTable: React.FC<VersionProps> = ({ selectedCountry, onCountryChange, onPublish, onResetDraft, onCopyFromGlobal, onResetToGlobal }) => {
  const pmsQuery = usePracticeManagementPorkParameters(selectedCountry);
  const { mutateAsync: saveDraft } = useSaveDraft(PRACTICE_MANAGEMENT_ENDPOINTS.PORK_PARAMETERS, PRACTICE_MANAGEMENT_PORK_PARAMETERS);

  return (
    <PracticeManagementTable
      endpoint={PRACTICE_MANAGEMENT_ENDPOINTS.PORK_PARAMETERS}
      data={pmsQuery.data ? [pmsQuery.data] : []}
      loading={pmsQuery.isLoading}
      onSaveDraft={(data, idsToDelete) => saveDraft({ data, idsToDelete })}
      onPublish={onPublish}
      versions={["draft"]}
      selectedVersion={"draft"}
      isDraftVersion={true}
      hasDraftVersion={false}
      selectedCountry={selectedCountry}
      onCountryChange={onCountryChange}
      onResetDraft={onResetDraft}
      onCopyFromGlobal={onCopyFromGlobal}
      onResetToGlobal={onResetToGlobal}
    />
  );
};

const VersionNotes = () => {
  const pmsQuery = useGetPracticeManagementVersionNotes();

  return <Table data={pmsQuery.data ?? []} rowHoverable />;
};

export const PracticeManagement: React.FC = () => {
  const navigate = useNavigate();
  const { tabKey } = useParams();
  const { user } = useAdminUserContext();
  const [currentTabKey, setCurrentTabKey] = useState<string>(tabKey ?? "crop-parameters");
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [selectedCountry, setSelectedCountry] = useState<PracticeManagement.CountryCode>("global");

  const versionsQuery = useGetPracticeManagementVersions();
  const { mutateAsync: publishDraft } = usePublishDraft();
  const { mutateAsync: resetDraft } = useResetDraft();
  const { mutateAsync: addCountry } = useAddNewCountry();
  const { mutateAsync: resetToGlobal } = useResetToGlobal();

  useEffect(() => {
    if (tabKey) setCurrentTabKey(tabKey);
    if (!tabKey || tabKey === "") navigate("crop-emission-factors");
  }, [navigate, tabKey]);

  const versions = versionsQuery.data ?? [];
  const effectiveVersion = selectedVersion ?? versions[0];
  const isDraftVersion = effectiveVersion === "draft";
  const hasDraftVersion = versions.includes("draft");
  const canEdit = hasAdminTierEqualOrAbove(user?.role, "tier2");
  const versionProps: VersionProps = {
    version: effectiveVersion,
    versions,
    isDraftVersion,
    hasDraftVersion,
    onVersionChange: setSelectedVersion,
    selectedCountry,
    onCountryChange: setSelectedCountry,
    onPublish: (formValues) =>
      publishDraft({
        ...formValues,
        proposedBy: formValues.proposedBy.join(", "),
        assessedBy: formValues.assessedBy.join(", "),
        decisionMadeBy: formValues.decisionMadeBy.join(", "),
      }),
    onResetDraft: resetDraft,
    onCopyFromGlobal: async () => {
      await addCountry(selectedCountry);
    },
    onResetToGlobal: async (endpoint) => {
      await resetToGlobal({ country: selectedCountry, endpoint });
    },
  };

  const tabChildren: Record<string, React.ReactNode> = {
    "crop-parameters": (
      <ErrorBoundary>
        <Typography variant="h5">
          Crop Parameters are the data used to ask farmers question about a crop. e.g. limits on how many tonnes/ha can be grown. Ranges are stored as [hardMin,
          softMin, softMax, hardMax]
        </Typography>
        <CropParametersTable {...versionProps} />
      </ErrorBoundary>
    ),
    "organic-fertilizer-parameters": (
      <ErrorBoundary>
        <Typography variant="h5">
          Organic Fertilizer Parameters are the data used to bound N input on the FE. e.g. nitrogen content per kg of fertilizer. Ranges are stored as [hardMin,
          softMin, softMax, hardMax]
        </Typography>
        <OrganicFertilizerParametersTable {...versionProps} />
      </ErrorBoundary>
    ),
    "piglet-parameters": (
      <ErrorBoundary>
        <Typography variant="h5">
          Piglet Parameters are the data used to bound piglet production inputs on the FE. e.g. average sale weight per piglet. Ranges are stored as [hardMin,
          softMin, softMax, hardMax]
        </Typography>
        <PigletParametersTable {...versionProps} />
      </ErrorBoundary>
    ),
    "pork-parameters": (
      <ErrorBoundary>
        <Typography variant="h5">
          Pork Parameters are the data used to bound pork production inputs on the FE. e.g. average slaughter weight per pig. Ranges are stored as [hardMin,
          softMin, softMax, hardMax]
        </Typography>
        <PorkParametersTable {...versionProps} />
      </ErrorBoundary>
    ),
    "crop-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">
          Crop Emission Factors are data used to calculate crop outcomes. e.g. the amount of diesel used per hectare when harvesting
        </Typography>
        <CropEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "mineral-fertilizer-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Mineral Fertilizer Emission Factors are data used to calculate footprinting outcomes</Typography>
        <MineralFertilizerEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "organic-fertilizer-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Organic Fertilizer Emission Factors are data used to calculate footprinting outcomes</Typography>
        <OrganicFertilizerEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "plant-protection-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Plant Protection Emission Factors are data used to calculate footprinting outcomes</Typography>
        <PlantProtectionEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "seeding-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Seeding Emission Factors are data used to calculate footprinting outcomes</Typography>
        <SeedingEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "tillage-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Tillage Emission Factors are data used to calculate footprinting outcomes</Typography>
        <TillageEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "soil-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Soil Emission Factors are data used to calculate footprinting outcomes</Typography>
        <SoilEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "utility-emission-factors": (
      <ErrorBoundary>
        <Typography variant="h5">Utility Emission Factors are data used to calculate footprinting outcomes</Typography>
        <UtilityEmissionFactorsTable {...versionProps} />
      </ErrorBoundary>
    ),
    "version-notes": (
      <ErrorBoundary>
        <Typography variant="h5">Version history is shown below</Typography>
        <VersionNotes />
      </ErrorBoundary>
    ),
  };

  const tabList = PRACTICE_MANAGEMENT_TABS.map(({ key, label }) => ({ key, label, children: tabChildren[key] }));

  if (!checkAdminAccess(user?.role, "practice-management/")) {
    return (
      <MainPage pageName="Admin Practice Management" fullWidth className="margin-300 margin-bottom-500">
        You don't have permission to view this page
      </MainPage>
    );
  }

  return (
    <MainPage pageName="Admin Practice Management" fullWidth className="margin-300 margin-bottom-500">
      {!canEdit && (
        <AlertBanner
          type="info"
          iconName="tabler/info-circle"
          description="Restricted access. Only admins with tier 2 access or higher can edit and publish content."
          className="margin-bottom-100"
        />
      )}
      <Tabs activeKey={currentTabKey} size="small" onChange={(v) => navigate({ pathname: `/practice-management/${v}` }, { replace: true })} items={tabList} />
    </MainPage>
  );
};
